import { create } from 'zustand';

/**
 * The simulation being built on the Certificates page (00-OVERVIEW D12, I4).
 * Nothing in it is real until recorded, and nothing done while simulating
 * touches real data — every edit lands here instead:
 *
 *   rows          simulated purchases `{ id, purchaseDate, quantity, pricePerCertificate | null }`
 *   overrides     changes to recorded purchases, by purchase id:
 *                 `{ [purchaseId]: { purchaseDate?, quantity?, pricePerCertificate? } }`
 *   removedIds    recorded purchases taken out in the simulation
 *
 * `loaded` is the saved simulation this came from (or null for a new one), kept
 * as it was saved so "modified" is a comparison, not a flag to maintain.
 * Modelled on `CbamCosts/stores/useSimulationStore.js`.
 */

let seq = 0;
const newRowId = () => `sim-${Date.now().toString(36)}-${(seq += 1)}`;

const empty = { rows: [], overrides: {}, removedIds: [] };
const initial = { ...empty, loaded: null };

/** A saved simulation as the store's working shape (also read by the planning-scenario comparison). */
export const fromSaved = (simulation) => ({
  rows: (simulation?.purchases ?? []).map((r) => ({ ...r })),
  overrides: Object.fromEntries(
    (simulation?.overrides ?? []).map(({ purchaseId, ...patch }) => [purchaseId, { ...patch }]),
  ),
  removedIds: [...(simulation?.removedIds ?? [])],
});

export const useCertificateSimulationStore = create((set) => ({
  ...initial,
  addRow: (row) => set((s) => ({ rows: [...s.rows, { id: newRowId(), ...row }] })),
  patchRow: (id, patch) =>
    set((s) => ({ rows: s.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) })),
  removeRow: (id) => set((s) => ({ rows: s.rows.filter((r) => r.id !== id) })),
  clearRows: () => set({ ...empty }),
  /** Change a recorded purchase inside the simulation only. */
  overridePurchase: (purchaseId, patch) =>
    set((s) => ({
      overrides: { ...s.overrides, [purchaseId]: { ...s.overrides[purchaseId], ...patch } },
    })),
  /** Take a recorded purchase out inside the simulation only. */
  removePurchase: (purchaseId) =>
    set((s) => ({ removedIds: [...new Set([...s.removedIds, purchaseId])] })),
  /** Undo every simulated change to a recorded purchase. */
  restorePurchase: (purchaseId) =>
    set((s) => {
      const { [purchaseId]: _dropped, ...overrides } = s.overrides;
      return { overrides, removedIds: s.removedIds.filter((id) => id !== purchaseId) };
    }),
  /** Start a new, unsaved simulation. */
  reset: () => set({ ...empty, loaded: null }),
  /** Open a saved simulation. */
  load: (simulation) => set({ ...fromSaved(simulation), loaded: simulation ?? null }),
  /** After a save, the saved copy is the new reference point. */
  markSaved: (simulation) => set({ ...fromSaved(simulation), loaded: simulation }),
}));

/** The simulation as the `/certificate-simulations` payload stores it. */
export const simulationPayload = (state) => ({
  purchases: state.rows.map(({ id, purchaseDate, quantity, pricePerCertificate }) => ({
    id,
    purchaseDate,
    quantity: Number(quantity),
    pricePerCertificate: pricePerCertificate ?? null,
  })),
  overrides: Object.entries(state.overrides).map(([purchaseId, patch]) => ({
    purchaseId,
    ...patch,
  })),
  removedIds: [...state.removedIds],
});

const comparable = (s) => {
  const p = simulationPayload(s);
  return JSON.stringify([
    p.purchases.map((r) => [r.id, r.purchaseDate, r.quantity, r.pricePerCertificate]),
    [...p.overrides].sort((a, b) => a.purchaseId.localeCompare(b.purchaseId)),
    [...p.removedIds].sort(),
  ]);
};

/** Whether the simulation changes anything at all. */
export const isSimulationActive = (state) =>
  state.rows.length > 0 || Object.keys(state.overrides).length > 0 || state.removedIds.length > 0;

/** Whether the working simulation differs from the saved one (or changes anything, if new). */
export const isSimulationModified = (state) =>
  state.loaded
    ? comparable(state) !== comparable(fromSaved(state.loaded))
    : isSimulationActive(state);

/**
 * The working rows as 2027 purchases, for the account figures. Rows dated
 * before `today` are left out: a simulation looks forward (D12).
 */
export const simulatedPurchases = (rows, today) =>
  (rows ?? [])
    .filter((r) => r.purchaseDate >= today && Number(r.quantity) > 0)
    .map((r) => ({
      _id: r.id,
      certificateYear: 2027,
      purchaseDate: r.purchaseDate,
      quantity: Number(r.quantity),
      pricePerCertificate: r.pricePerCertificate ?? null,
      simulated: true,
    }));

/**
 * Recorded purchases as the simulation sees them: removed ones flagged
 * `simRemoved` (kept for the table, left out of the figures by the caller),
 * overridden ones carrying the new values and `simChanged: { field: true }`.
 */
export const applySimulation = (purchases, { overrides = {}, removedIds = [] } = {}) =>
  (purchases ?? []).map((p) => {
    if (removedIds.includes(p._id)) return { ...p, simRemoved: true };
    const patch = overrides[p._id];
    if (!patch) return p;
    const simChanged = Object.fromEntries(
      Object.keys(patch)
        .filter((k) => patch[k] !== p[k])
        .map((k) => [k, true]),
    );
    return { ...p, ...patch, simChanged };
  });

export default useCertificateSimulationStore;
